import { useState, type ComponentProps } from "react"
import { format } from "date-fns"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Chart from "@/v2-ui/chart"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import {
  formatAppArea,
  formatAppCode,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  getAreaDayApps,
  type UsageAreaDayRow,
} from "@/v2-backoffice-client/usage/usage.utils"
import {
  deriveUsageActivityChartPoints,
  deriveUsageActivityHolidayBands,
  TREND_WINDOW_DAYS,
  type UsageActivityChartPoint,
} from "@/v2-backoffice-client/usage/usage.activityChart.utils"

const RANGE_OPTIONS = [
  { label: "4 weeks", value: 28 },
  { label: "13 weeks", value: 90 },
]

// Brand data green, not the theme's `--primary` (amber chrome in the
// backoffice theme): data series read apart from buttons and chips.
const CHART_CONFIG = {
  value: {
    label: "Weighted activity",
    color: DATA_VIZ.brand.green2,
  },
}

export type UsageActivityChartProps = ComponentProps<"div"> & {
  /** Raw per-day, per-area rows from the usage queries (90-day window). */
  areaDays: UsageAreaDayRow[]
  /**
   * Fixed window length in days. When set, the built-in 4/13-week range
   * switcher is hidden and the chart pads exactly this many days — the
   * Portfolio's global timeframe drives it instead.
   */
  windowDays?: number
  /** Last day of the fixed window (defaults to today); pairs with windowDays. */
  windowEnd?: Date
  /**
   * Hide the built-in title/caption/legend row — for hosts that render
   * their own section header (the Portfolio hero) and would duplicate it.
   */
  hideHeader?: boolean
}

// Daily weighted-activity bar chart with a real hover tooltip (date, the
// weighted + raw numbers, and what the activity was about that day),
// modeled on the uptime response chart: responsive container, range
// switcher, quiet days rendered as gaps instead of being skipped. When more
// than one product contributed (Console v1 + v2), the bars stack per app so
// the product split is visible day by day.
function UsageActivityChart(props: UsageActivityChartProps) {
  const {
    areaDays,
    windowDays,
    windowEnd,
    hideHeader,
    className,
    ...divProps
  } = props

  const [rangeDays, setRangeDays] = useState(RANGE_OPTIONS[0].value)
  const effectiveDays = windowDays ?? rangeDays

  // Built from props alone, no memo: Recharts restarts the bar animation
  // whenever `data` changes identity, so a host that re-renders for reasons
  // of its own should hold its state below this component, not above it.
  const apps = getAreaDayApps(areaDays)
  const isStacked = apps.length > 1
  const chartData = deriveUsageActivityChartPoints({
    areaDays,
    apps,
    days: effectiveDays,
    end: windowEnd,
  })
  const hasData = chartData.some((point) => point.value > 0)
  const holidayBands = deriveUsageActivityHolidayBands(chartData)

  return (
    <div className={cn("flex flex-col gap-2", className)} {...divProps}>
      {!hideHeader && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h3 className="text-sm font-semibold">Daily activity</h3>
            <span
              className="text-xs text-muted-foreground"
              title="Weighted = heavy actions (exports, reports, saves) count more than clicks."
            >
              weighted usage actions per day
            </span>
            {isStacked && (
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                {apps.map((app) => (
                  <span key={app} className="flex items-center gap-1">
                    <span
                      className="size-2 rounded-full"
                      style={{ backgroundColor: getAppCodeColor(app) }}
                    />
                    {formatAppCode(app)}
                  </span>
                ))}
              </span>
            )}
            {hasData && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  aria-hidden
                  className="inline-block w-4 border-t-2 border-dashed border-foreground/60"
                />
                {TREND_WINDOW_DAYS}-day trend
              </span>
            )}
          </div>
          {windowDays == null && (
            <div className="inline-flex items-center rounded-md border border-border overflow-hidden text-xs">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={cn(
                    "px-3 py-1 transition-colors",
                    rangeDays === opt.value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                  onClick={() => setRangeDays(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!hasData ? (
        <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          No activity in the selected window.
        </p>
      ) : (
        // A fixed height: the chart base carries aspect-video, and as a
        // growing flex item that intrinsic ratio would size it from the
        // width — a wide panel got a 500 px chart.
        <Chart config={CHART_CONFIG} className="h-[200px] w-full shrink-0">
          {/* Composed, not Bar: the trendline rides over the daily bars. */}
          <Chart.ComposedChart
            data={chartData}
            margin={{ left: -18, right: 4 }}
          >
            <Chart.Grid vertical={false} />
            {/* X keys on the ISO day (unique even across a 1-year window
                where "1. Jul" repeats); ticks format on render. */}
            <Chart.Axis.X
              dataKey="day"
              tickFormatter={(value: string) =>
                format(new Date(value), "d. MMM")
              }
              tickLine={false}
              axisLine={false}
              tickMargin={6}
              minTickGap={28}
              className="text-xs text-muted-foreground"
            />
            {holidayBands.map((band) => (
              <Chart.ReferenceArea
                key={band.key}
                x1={band.x1}
                x2={band.x2}
                fill="currentColor"
                className="text-muted-foreground"
                fillOpacity={0.07}
                label={{
                  value: band.label,
                  position: "insideTop",
                  fontSize: 10,
                  className: "fill-muted-foreground",
                }}
              />
            ))}
            <Chart.Axis.Y
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              width={48}
              allowDecimals={false}
              className="text-xs text-muted-foreground"
            />
            <Chart.Tooltip content={<UsageActivityTooltip apps={apps} />} />
            {isStacked ? (
              apps.map((app, index) => (
                <Chart.Bar
                  key={app}
                  dataKey={`app:${app}`}
                  stackId="apps"
                  fill={getAppCodeColor(app)}
                  radius={index === apps.length - 1 ? [2, 2, 0, 0] : 0}
                  maxBarSize={18}
                />
              ))
            ) : (
              <Chart.Bar
                dataKey="value"
                fill="var(--color-value)"
                radius={[2, 2, 0, 0]}
                maxBarSize={18}
              />
            )}
            {/* Neutral + dashed so it reads as a reference over the data
                bars, not as another data series. */}
            <Chart.Line
              dataKey="trend"
              type="monotone"
              stroke="hsl(var(--foreground))"
              strokeOpacity={0.55}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          </Chart.ComposedChart>
        </Chart>
      )}
    </div>
  )
}

// Custom tooltip: "what happened that day", not just a number — weighted
// total, raw event count, the per-product split when both consoles
// contributed, and the day's leading interest areas.
function UsageActivityTooltip(props: {
  active?: boolean
  payload?: { payload: UsageActivityChartPoint }[]
  apps?: string[]
}) {
  const { active, payload, apps = [] } = props
  const point = payload?.[0]?.payload
  if (!active || !point) return null

  const appSplit = apps
    .map((app) => ({ app, value: Number(point[`app:${app}`] ?? 0) }))
    .filter((entry) => entry.value > 0)

  return (
    <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md">
      <p className="font-medium">
        {format(new Date(point.day), "EEEE d. MMM")}
      </p>
      {point.value > 0 ? (
        <>
          <p className="text-muted-foreground">
            {formatNumber(point.value)} weighted activity ·{" "}
            {formatNumber(point.rawCount)} events
          </p>
          <p className="text-muted-foreground">
            {TREND_WINDOW_DAYS}-day trend: {formatNumber(point.trend)}
          </p>
          {appSplit.length > 1 && (
            <p className="text-muted-foreground">
              {appSplit
                .map(
                  (entry) =>
                    `${formatAppCode(entry.app)} ${formatNumber(entry.value)}`,
                )
                .join(" · ")}
            </p>
          )}
          {point.topAreas.length > 0 && (
            <p className="text-muted-foreground">
              {point.topAreas
                .map((entry) => formatAppArea(entry.area))
                .join(" · ")}
            </p>
          )}
        </>
      ) : (
        <p className="text-muted-foreground">
          No activity
          {point.trend > 0 && (
            <>
              {" "}
              · {TREND_WINDOW_DAYS}-day trend {formatNumber(point.trend)}
            </>
          )}
        </p>
      )}
    </div>
  )
}

export default UsageActivityChart
